import { useCallback, useEffect, useMemo, useRef } from "react";
import useSWR from "swr";
import {
  type ContractReviewMessage,
  type ContractReviewResponse,
} from "../../amplify/backend/function/OfferExpCheckout/ts/public/types";
import {
  getContractReview,
  startContractReview,
} from "services/contractReview";
import { useAuthToken } from "hooks/useAuthToken";

const POLL_INTERVAL_MS = 30_000;

/** Temporary: contract-review API may return `messages` as nested arrays until the backend sends a flat list. */
const flattenContractReviewMessages = (
  raw: ContractReviewMessage[] | undefined
): ContractReviewMessage[] => {
  if (!raw?.length) return [];
  return raw.flat();
};

const contractReviewFetcher = async ([, authKey, proposalId]: [
  string,
  string,
  string
]): Promise<ContractReviewResponse> => {
  try {
    return await getContractReview(authKey, proposalId);
  } catch (err) {
    const statusCode = (err as { response?: { statusCode?: number } })?.response
      ?.statusCode;
    if (statusCode === 404) {
      return await startContractReview(authKey, proposalId);
    }
    throw err;
  }
};

const useContractReview = (proposalId: string | undefined) => {
  const authKey = useAuthToken("HYBRID")?.token;
  const shouldFetch = Boolean(proposalId && authKey);

  const lastCompletedMessagesRef = useRef<ContractReviewMessage[]>([]);
  const isBackgroundRefreshInFlightRef = useRef(false);

  const { data, error, isLoading, mutate } = useSWR(
    shouldFetch ? ["Contract Review", authKey!, proposalId!] : null,
    contractReviewFetcher,
    {
      refreshInterval: (latestData?: ContractReviewResponse) => {
        if (isBackgroundRefreshInFlightRef.current) {
          return 0;
        }
        if (
          latestData?.result === "started" ||
          latestData?.result === "submitted"
        ) {
          return POLL_INTERVAL_MS;
        }
        return 0;
      },
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      keepPreviousData: true,
    }
  );

  // One automatic background restart per mount (reset when the hook unmounts).
  const shouldTriggerRestartReview = useRef(true);

  const restartReview = useCallback(async () => {
    if (!authKey || !proposalId) return;
    isBackgroundRefreshInFlightRef.current = true;
    try {
      const response = await startContractReview(authKey, proposalId);
      if (response.result === "completed") {
        lastCompletedMessagesRef.current = flattenContractReviewMessages(
          response.messages
        );
      }
      await mutate(response, { revalidate: false });
    } finally {
      isBackgroundRefreshInFlightRef.current = false;
    }
  }, [authKey, proposalId, mutate]);

  useEffect(() => {
    // Server review in flight — rely on SWR polling; do not POST again on completion.
    if (data?.result === "started" || data?.result === "submitted") {
      shouldTriggerRestartReview.current = false;
    }

    // Existing completed review — refresh once in the background for stale data.
    if (
      data?.result === "completed" &&
      data.messages?.flat().length > 0 &&
      shouldTriggerRestartReview.current
    ) {
      shouldTriggerRestartReview.current = false;
      void restartReview();
    }
  }, [data, restartReview]);

  const isReviewInProgress =
    data?.result === "started" || data?.result === "submitted";

  const messages: ContractReviewMessage[] = useMemo(() => {
    if (data?.result === "completed") {
      const flattened = flattenContractReviewMessages(data.messages);
      lastCompletedMessagesRef.current = flattened;
      return flattened;
    }
    return lastCompletedMessagesRef.current;
  }, [data]);

  return { messages, isLoading, error, restartReview, isReviewInProgress };
};

export { useContractReview };
